import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import {
  auth,
  db,
  getDownloadURL,
  ref,
  storage,
  uploadBytes,
} from "../../../../../backend/firebaseConfig";

const ADVISERS_PATH = ["Authorized Members", "Advisers", "Data"];
const CPD_RECORDS_COLLECTION = "CPD Records";
export const CPD_REPORT_UPLOADS_COLLECTION = "CPD Report Uploads";

export const CPD_REPORT_TYPES = {
  all: "All CPD",
  idd: "IDD CPD",
};

const COLUMN_MAP = {
  "First name": "firstName",
  "Last name": "lastName",
  "User title": "userTitle",
  "CPD entries": "cpdEntries",
  "CPD minutes": "cpdMinutes",
  "External PTS entries": "externalPtsEntries",
  "External PTS minutes": "externalPtsMinutes",
  "Structured Entries": "structuredEntries",
  "Structured Minutes": "structuredMinutes",
  "Non-Structured Entries": "nonStructuredEntries",
  "Non-Structured Minutes": "nonStructuredMinutes",
  "Learning entries": "learningEntries",
  "Learning minutes": "learningMinutes",
  "Test entries": "testEntries",
  "Test minutes": "testMinutes",
  "PDP entries": "pdpEntries",
  "PDP minutes": "pdpMinutes",
  "External eEntries": "externalEntries",
  "External mMinutes": "externalMinutes",
  "Other entries": "otherEntries",
  "Other minutes": "otherMinutes",
  Documents: "documents",
  UserRef: "userRef",
  "User Email Address": "email",
  "Team name": "teamName",
  FCANumber: "fcaNumber",
  Postcode: "postcode",
};

const NUMBER_FIELDS = new Set([
  "cpdEntries",
  "cpdMinutes",
  "externalPtsEntries",
  "externalPtsMinutes",
  "structuredEntries",
  "structuredMinutes",
  "nonStructuredEntries",
  "nonStructuredMinutes",
  "learningEntries",
  "learningMinutes",
  "testEntries",
  "testMinutes",
  "pdpEntries",
  "pdpMinutes",
  "externalEntries",
  "externalMinutes",
  "otherEntries",
  "otherMinutes",
  "documents",
]);

const normaliseEmail = (value) => String(value || "").trim().toLowerCase();

const toNumber = (value) => {
  const number = Number(String(value ?? "").trim());
  return Number.isFinite(number) ? number : 0;
};

const isoDateFromParts = (year, month, day) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() + 1 !== month ||
    date.getUTCDate() !== day
  ) {
    return "";
  }

  return `${String(year).padStart(4, "0")}-${String(month).padStart(
    2,
    "0",
  )}-${String(day).padStart(2, "0")}`;
};

export const normaliseCpdDate = (value) => {
  if (!value) return "";

  const text = String(value).trim();
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return isoDateFromParts(
      Number(isoMatch[1]),
      Number(isoMatch[2]),
      Number(isoMatch[3]),
    );
  }

  const ukMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (ukMatch) {
    return isoDateFromParts(
      Number(ukMatch[3]),
      Number(ukMatch[2]),
      Number(ukMatch[1]),
    );
  }

  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return isoDateFromParts(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
  );
};

export const getCpdReportDateFromFileName = (fileName = "") => {
  const match = String(fileName).match(/(\d{2})[_-](\d{2})[_-](\d{4})/);
  if (!match) return new Date().toISOString().slice(0, 10);
  return (
    normaliseCpdDate(`${match[1]}/${match[2]}/${match[3]}`) ||
    new Date().toISOString().slice(0, 10)
  );
};

export const createEmptyCpdRecord = ({
  reportType = "all",
  reportDate = "",
} = {}) => ({
  reportType,
  reportDate,
  firstName: "",
  lastName: "",
  email: "",
  uploadedEmail: "",
  userTitle: "",
  cpdEntries: 0,
  cpdMinutes: 0,
  externalPtsEntries: 0,
  externalPtsMinutes: 0,
  structuredEntries: 0,
  structuredMinutes: 0,
  nonStructuredEntries: 0,
  nonStructuredMinutes: 0,
  learningEntries: 0,
  learningMinutes: 0,
  testEntries: 0,
  testMinutes: 0,
  pdpEntries: 0,
  pdpMinutes: 0,
  externalEntries: 0,
  externalMinutes: 0,
  otherEntries: 0,
  otherMinutes: 0,
  documents: 0,
  userRef: "",
  teamName: "",
  fcaNumber: "",
  postcode: "",
});

export const parseCpdCsvText = (csvText, { reportType, fileName = "" }) => {
  const parsed = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => String(header || "").trim(),
  });

  if (parsed.errors?.length) {
    throw new Error(
      parsed.errors[0]?.message || "The CPD CSV could not be read.",
    );
  }

  const headers = parsed.meta?.fields || [];
  if (!headers.includes("User Email Address")) {
    throw new Error("The CPD report must include a User Email Address column.");
  }

  const reportDate = getCpdReportDateFromFileName(fileName);

  return (parsed.data || []).map((source, index) => {
    const record = createEmptyCpdRecord({ reportType, reportDate });

    Object.entries(COLUMN_MAP).forEach(([column, field]) => {
      const value = source[column];
      record[field] = NUMBER_FIELDS.has(field)
        ? toNumber(value)
        : String(value ?? "").trim();
    });

    record.email = normaliseEmail(record.email);
    record.sourceFileName = fileName;
    record.sourceRowNumber = index + 2;

    return record;
  });
};

const parseCpdExcelFile = async (file, reportType) => {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const sheetName = workbook.SheetNames[0];

  if (!sheetName) {
    throw new Error("The CPD workbook does not contain a worksheet.");
  }

  const csvText = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName]);
  return parseCpdCsvText(csvText, {
    reportType,
    fileName: file.name,
  });
};

const parseCpdReportFile = async (file, reportType) => {
  const extension = String(file?.name || "")
    .split(".")
    .pop()
    .toLowerCase();

  if (extension === "csv") {
    return parseCpdCsvText(await file.text(), {
      reportType,
      fileName: file.name,
    });
  }

  if (extension === "xlsx" || extension === "xls") {
    return parseCpdExcelFile(file, reportType);
  }

  throw new Error("CPD reports must be CSV, XLSX or XLS files.");
};

const loadAdviserDirectory = async () => {
  const snapshot = await getDocs(collection(db, ...ADVISERS_PATH));
  const advisersByEmail = new Map();
  const adviserOptions = [];

  snapshot.docs.forEach((adviserDocument) => {
    const adviser = adviserDocument.data() || {};
    const firstName =
      adviser.preferredName ||
      adviser.firstName ||
      adviser.forename ||
      adviser.personalDetails?.forename ||
      "";
    const lastName =
      adviser.lastName ||
      adviser.surname ||
      adviser.personalDetails?.surname ||
      "";
    const storedEmail = normaliseEmail(
      adviser.email || adviser.Email || adviserDocument.id,
    );
    const entry = {
      adviserId: adviserDocument.id,
      storedEmail,
      firstName,
      lastName,
    };

    [adviserDocument.id, adviser.email, adviser.Email]
      .map(normaliseEmail)
      .filter(Boolean)
      .forEach((email) => advisersByEmail.set(email, entry));

    if (storedEmail) {
      const displayName =
        [firstName, lastName].filter(Boolean).join(" ").trim() ||
        storedEmail;
      adviserOptions.push({
        id: adviserDocument.id,
        name: `${displayName} (${storedEmail})`,
        email: storedEmail,
        firstName,
        lastName,
      });
    }
  });

  adviserOptions.sort((a, b) => a.name.localeCompare(b.name));

  return { advisersByEmail, adviserOptions };
};

const buildAdviserDirectory = (advisers = []) => {
  const advisersByEmail = new Map();
  const adviserOptions = [];

  advisers.forEach((adviser) => {
    const adviserId = adviser.adviserId || adviser.id || "";
    const storedEmail = normaliseEmail(adviser.email || adviser.id);
    const firstName = adviser.firstName || adviser.preferredName || "";
    const lastName = adviser.lastName || "";
    const entry = {
      adviserId,
      storedEmail,
      firstName,
      lastName,
    };

    [adviser.email, adviser.id, adviser.adviserId]
      .map(normaliseEmail)
      .filter(Boolean)
      .forEach((email) => advisersByEmail.set(email, entry));

    if (adviserId && storedEmail) {
      const displayName =
        adviser.name ||
        [firstName, lastName].filter(Boolean).join(" ").trim() ||
        storedEmail;
      adviserOptions.push({
        id: adviserId,
        name: `${displayName} (${storedEmail})`,
        email: storedEmail,
        firstName,
        lastName,
      });
    }
  });

  adviserOptions.sort((a, b) => a.name.localeCompare(b.name, "en-GB"));
  return { advisersByEmail, adviserOptions };
};

export const previewCpdReportFiles = async ({
  allFile,
  iddFile,
  advisers,
}) => {
  if (!allFile || !iddFile) {
    throw new Error(
      "Choose both the All CPD report and the IDD report before reviewing.",
    );
  }

  const sources = await Promise.all(
    [
      { file: allFile, reportType: "all" },
      { file: iddFile, reportType: "idd" },
    ].map(async ({ file, reportType }) => ({
        reportType,
        fileName: file.name,
        records: await parseCpdReportFile(file, reportType),
      })),
  );

  const { advisersByEmail, adviserOptions } = Array.isArray(advisers)
    ? buildAdviserDirectory(advisers)
    : await loadAdviserDirectory();
  const seen = new Set();
  const rows = sources.flatMap((source) =>
    source.records.map((record) => {
      const duplicateKey = `${source.reportType}:${record.email}`;
      const adviser = advisersByEmail.get(record.email);
      let status = "Matched";
      let reason = "Ready to add";

      if (!record.email) {
        status = "Unmatched";
        reason = "Email address is missing.";
      } else if (seen.has(duplicateKey)) {
        status = "Duplicate";
        reason = "This email appears more than once in the same report.";
      } else if (!adviser) {
        status = "Unmatched";
        reason = "No adviser was found with this email address.";
      }

      seen.add(duplicateKey);

      return {
        ...record,
        uploadedEmail: record.email,
        adviserId: adviser?.adviserId || "",
        storedEmail: adviser?.storedEmail || "",
        status,
        reason,
      };
    }),
  );

  return {
    sources: sources.map(({ reportType, fileName, records }) => ({
      reportType,
      fileName,
      rowCount: records.length,
    })),
    rows,
    adviserOptions,
    matchedRows: rows.filter((row) => row.status === "Matched"),
    unmatchedRows: rows.filter((row) => row.status !== "Matched"),
  };
};

const cleanRecordForSave = (record = {}) => {
  const cleaned = createEmptyCpdRecord({
    reportType: record.reportType,
    reportDate: record.reportDate,
  });

  Object.keys(cleaned).forEach((field) => {
    cleaned[field] = NUMBER_FIELDS.has(field)
      ? toNumber(record[field])
      : String(record[field] ?? "").trim();
  });

  cleaned.email = normaliseEmail(record.email);
  cleaned.uploadedEmail = normaliseEmail(
    record.uploadedEmail || record.email,
  );
  cleaned.reportDate = normaliseCpdDate(record.reportDate);
  cleaned.sourceFileName = String(record.sourceFileName || "").trim();
  cleaned.sourceRowNumber = toNumber(record.sourceRowNumber);
  cleaned.sourceType = record.sourceType === "manual" ? "manual" : "csv";

  return cleaned;
};

const addRecordToBatch = (batch, { adviserId, record }) => {
  const cleaned = cleanRecordForSave(record);
  if (!cleaned.reportDate) {
    throw new Error("Enter a valid report date.");
  }
  const licenseRef = doc(
    db,
    ...ADVISERS_PATH,
    adviserId,
    "License Certificates",
    "Data",
  );
  const recordRef = doc(collection(licenseRef, CPD_RECORDS_COLLECTION));
  const audit = {
    importedAt: serverTimestamp(),
    importedBy: auth.currentUser?.email || "",
    importedByName: auth.currentUser?.displayName || "",
  };

  batch.set(recordRef, { ...cleaned, adviserId, ...audit }, { merge: true });
  batch.set(
    licenseRef,
    {
      cpd: {
        [cleaned.reportType]: {
          ...cleaned,
          adviserId,
          ...audit,
        },
      },
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.email || "",
    },
    { merge: true },
  );
};

const safeStorageFileName = (fileName = "report") =>
  String(fileName)
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "report";

const uploadCpdReportFile = async ({ file, reportType, uploadId }) => {
  const storagePath = [
    "Compliance",
    "LicenceCertificates",
    "CPDReports",
    uploadId,
    `${reportType}-${safeStorageFileName(file.name)}`,
  ].join("/");
  const fileRef = ref(storage, storagePath);

  await uploadBytes(fileRef, file, {
    contentType: file.type || "application/octet-stream",
    contentDisposition: `attachment; filename="${safeStorageFileName(file.name)}"`,
  });

  return {
    reportType,
    name: file.name,
    type: file.type || "application/octet-stream",
    size: Number(file.size || 0),
    storagePath,
    url: await getDownloadURL(fileRef),
  };
};

export const commitCpdReportPreview = async (
  preview,
  { allFile, iddFile } = {},
) => {
  const matchedRows = Array.isArray(preview?.matchedRows)
    ? preview.matchedRows
    : [];

  if (!matchedRows.length) {
    throw new Error("There are no matched CPD records to add.");
  }

  if (!allFile || !iddFile) {
    throw new Error(
      "Both source reports are required so they can be saved in the upload history.",
    );
  }

  const uploadLogRef = doc(collection(db, CPD_REPORT_UPLOADS_COLLECTION));
  const uploadedFiles = await Promise.all([
    uploadCpdReportFile({ file: allFile, reportType: "all", uploadId: uploadLogRef.id }),
    uploadCpdReportFile({ file: iddFile, reportType: "idd", uploadId: uploadLogRef.id }),
  ]);

  const chunkSize = 225;
  let written = 0;

  for (let index = 0; index < matchedRows.length; index += chunkSize) {
    const batch = writeBatch(db);
    const chunk = matchedRows.slice(index, index + chunkSize);
    chunk.forEach((row) =>
      addRecordToBatch(batch, {
        adviserId: row.adviserId,
        record: { ...row, sourceType: "csv" },
      }),
    );
    await batch.commit();
    written += chunk.length;
  }

  const logBatch = writeBatch(db);
  logBatch.set(uploadLogRef, {
    importedAt: serverTimestamp(),
    importedBy: auth.currentUser?.email || "",
    importedByName: auth.currentUser?.displayName || "",
    files: uploadedFiles,
    reports: preview.sources || [],
    matchedCount: written,
    unmatchedCount: preview.unmatchedRows?.length || 0,
    totalRowCount: preview.rows?.length || 0,
  });
  await logBatch.commit();

  return {
    written,
    unmatched: preview.unmatchedRows?.length || 0,
    uploadedFiles,
  };
};

export const saveManualCpdRecord = async ({ adviserId, record }) => {
  if (!adviserId) throw new Error("Select an adviser.");
  if (!record?.email) {
    throw new Error("The selected adviser needs an email address.");
  }
  const reportDate = normaliseCpdDate(record?.reportDate);
  if (!reportDate) throw new Error("Enter a valid report date.");

  const batch = writeBatch(db);
  addRecordToBatch(batch, {
    adviserId,
    record: { ...record, reportDate, sourceType: "manual" },
  });
  await batch.commit();
  return cleanRecordForSave({ ...record, reportDate });
};

export const formatCpdHours = (minutes) => {
  const hours = toNumber(minutes) / 60;
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(2);
};
